/**
 * Cruise control.
 *
 * Point at something, switch it on, and the ship flies there and stops. It
 * exists because the distances are deliberately unreasonable: an adjacent
 * planetary orbit is seventeen thousand units away, which is minutes of holding
 * W at a fixed heading. That is not a decision, it is a chore, and the warp
 * drive only shortens it.
 *
 * What cruise is *not* is a solver. It flies straight at the thing, and if
 * something is in the way you hit it. Obstacle avoidance would make it a
 * feature you could stop paying attention to, and the point is that you can
 * take your hands off the stick, not that you can stop looking out of the
 * window.
 *
 * The rules live here rather than in the flight loop because they are all
 * arithmetic — when to brake, when it is worth lighting the warp drive, when
 * the autopilot has lost the plot — and arithmetic can be tested without a
 * camera.
 */
import { IDLE_SNAP_SPEED } from './flight.js';
/**
 * How much further back than the minimum cruise tries to stop.
 *
 * Parking on the closest fully-held point put the ship right on top of what it
 * had flown to. This buys room to look at the thing you have arrived at.
 */
export const CRUISE_CLEARANCE = 50;
/**
 * How far inside a body's reach cruise is willing to sit.
 *
 * Not right on the edge: the lock is what stops the ship sliding, and parking
 * on the boundary of it means one nudge puts you outside.
 */
const REACH_MARGIN = 0.9;
/**
 * Where cruise aims to stop.
 *
 * Anywhere inside a body's reach is held completely — the flight loop inherits
 * a locked body's motion whole rather than in proportion to the grip, because
 * proportional inheritance is a permanent slide dressed up as a soft hold. That
 * makes this simple: back off by the clearance, and stop short of the boundary
 * so a nudge does not put you outside it.
 *
 * An earlier version had to ration the clearance against the strength curve,
 * which meant a station granted about two units of it. It now gets the lot.
 */
export function cruiseStandoff(radius, reach, clearance = CRUISE_CLEARANCE) {
    const reachDistance = radius * reach;
    const held = radius + (reachDistance - radius) * 0.5;
    return Math.min(held + clearance, reachDistance * REACH_MARGIN);
}
/**
 * How much room it takes to shed this speed.
 *
 * Plain kinematics, `v² / 2a`. It is the number the whole autopilot turns on:
 * cruise runs at full throttle until this reaches the distance left, and the
 * consequence at warp speed is that braking begins a very long way out.
 */
export function brakingDistance(speed, deceleration) {
    if (deceleration <= 0)
        return Infinity;
    return (speed * speed) / (2 * deceleration);
}
/**
 * Margin on the braking distance.
 *
 * Braking is computed from the ship's rated deceleration, and the ship does not
 * always get it — thrust is scaled by power satisfaction, the frame step
 * varies, and a body you are closing on is itself moving. Starting a fifth
 * early costs a few seconds; starting late costs an overshoot and a turn.
 */
const BRAKING_MARGIN = 1.2;
/**
 * The fastest speed from which the ship could still stop in the distance left.
 *
 * This replaced a bang-bang throttle — full ahead until the braking distance
 * caught up, then nothing — which looked correct and chattered badly. The two
 * states are unstable against each other: braking shortens the braking
 * distance, which re-permits full throttle, which lengthens it again. The lever
 * flickered between one and zero several times a second, and every flicker was
 * a real reversal of thrust.
 *
 * Inverting the same kinematics gives a speed rather than a decision, so the
 * throttle becomes continuous and settles instead of hunting. It is also no
 * slower: this *is* the fastest arrival, because at every instant the ship is
 * going as fast as it could and still stop.
 */
export function cruiseTargetSpeed(remaining, deceleration) {
    if (remaining <= 0 || deceleration <= 0)
        return 0;
    return Math.sqrt((2 * deceleration * remaining) / BRAKING_MARGIN);
}
/**
 * That speed as a throttle setting, which is what the ship actually takes.
 *
 * Saturates at one for most of a long leg — there is nothing gradual about the
 * first ten thousand units — and eases off only over the final approach.
 */
export function cruiseThrottle(remaining, deceleration, maxSpeed) {
    if (maxSpeed <= 0)
        return 0;
    return Math.min(1, cruiseTargetSpeed(remaining, deceleration) / maxSpeed);
}
/**
 * Whether the trip is over.
 *
 * The throttle approaches zero rather than reaching it, so "remaining <= 0" is
 * a condition that never quite arrives — and the main drive's own dead band
 * brings the ship to a stop a couple of units short of the mark, at which point
 * nothing is left to close the gap. Cruise would then hold on for ever with the
 * lever at a hundredth, waiting for a mass lock to rescue it.
 *
 * So arrival is the point at which the drive can no longer ask for anything: a
 * target speed inside the dead band *is* a command to stop. A sluggish hull
 * gives up further out than a nimble one, which is right — it is the one that
 * cannot make fine corrections.
 */
export function cruiseArrived(remaining, deceleration) {
    return cruiseTargetSpeed(remaining, deceleration) <= IDLE_SNAP_SPEED;
}
/**
 * How long a leg takes from rest, accelerating, holding, and braking again.
 *
 * An *asymmetric* trapezoid, degenerating to a triangle on a hop too short to
 * reach top speed. The two rates are separate because in warp they genuinely
 * are: a hull winds up faster than it sheds, so assuming one number would
 * under-count exactly the mode this is mostly used to judge.
 */
export function tripTime(distance, acceleration, deceleration, maxSpeed) {
    if (distance <= 0)
        return 0;
    if (acceleration <= 0 || deceleration <= 0 || maxSpeed <= 0)
        return Infinity;
    // Distance spent winding up and shedding it again, if it gets that far.
    const ramp = (maxSpeed * maxSpeed) / (2 * acceleration) + (maxSpeed * maxSpeed) / (2 * deceleration);
    if (distance <= ramp) {
        // Never reaches the ceiling: it turns round at whatever speed the distance
        // affords, and the two ramps have to share it.
        const peak = Math.sqrt((2 * distance) / (1 / acceleration + 1 / deceleration));
        return peak / acceleration + peak / deceleration;
    }
    return distance / maxSpeed + maxSpeed / (2 * acceleration) + maxSpeed / (2 * deceleration);
}
/**
 * How much faster warp must be before cruise bothers.
 *
 * A hair, only enough to stop the decision flickering either side of the
 * crossover — the whole point is that it lights the drive whenever the drive is
 * the quicker way there.
 */
const WARP_MARGIN = 0.95;
/**
 * Whether cruise should be running the warp drive over this distance.
 *
 * This compares the two trips, which sounds obvious and replaced something that
 * was not: a rule of thumb saying "more than two and a half braking distances".
 * That fired **nineteen times too late** at every hull weight — warp genuinely
 * pays from about ten thousand units on a typical ship, and the heuristic held
 * out for two hundred thousand. Cruise crawled the whole way at thruster speed
 * while the drive sat idle, which is exactly what it exists not to do.
 */
export function cruiseShouldWarp(remaining, cruise, warp) {
    return (tripTime(remaining, warp.acceleration, warp.deceleration, warp.maxSpeed) <
        tripTime(remaining, cruise.acceleration, cruise.deceleration, cruise.maxSpeed) * WARP_MARGIN);
}
/**
 * Whether cruise may be switched on at all.
 *
 * The same gate as the warp drive, minus the speed: cruise is going to want
 * warp on any leg worth engaging it for, so letting it start on the precision
 * set or with the gear down only produces an autopilot that sets off at a crawl
 * and never explains why. Refusing up front, with the reason on the button, is
 * the honest version.
 */
export function canEngageCruise(set, landingGear, massLock, hasTarget) {
    if (set !== 'main')
        return { engaged: false, blocked: 'main drive required' };
    if (landingGear)
        return { engaged: false, blocked: 'gear down' };
    if (massLock > 0.05)
        return { engaged: false, blocked: 'mass locked' };
    if (!hasTarget)
        return { engaged: false, blocked: 'no target' };
    return { engaged: true, blocked: null };
}
/**
 * How far off course cruise tolerates before it gives up, in radians.
 *
 * Measured between where the ship is actually *going* and where the target is —
 * not between the nose and the target, which says nothing about a ship that is
 * sliding sideways. Eighteen degrees is past anything the autopilot's own
 * steering produces, so this only fires when something else is happening: the
 * pilot has taken the stick, or a mass lock is dragging the ship off its line.
 */
export const CRUISE_ABORT_ANGLE = Math.PI / 10;
/**
 * Whether the ship has strayed far enough off its line to abandon the cruise.
 *
 * Below `minSpeed` the velocity vector is noise — a ship at rest is not
 * "going" anywhere, and normalising a near-zero vector yields a direction that
 * flips about at random. Cruise would abort itself on the frame it started.
 */
export function cruiseHasStrayed(angleToTarget, speed, minSpeed = 1) {
    if (speed < minSpeed)
        return false;
    return Math.abs(angleToTarget) > CRUISE_ABORT_ANGLE;
}
//# sourceMappingURL=cruise.js.map